import React from "react";
import { BedDouble, Moon, Mail, Sparkles } from "lucide-react";

const inputCls = "w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30";

export const roomTuition = (offering, form) => {
  const opt = (offering.room_options || []).find((o) => o.key === form.room_key);
  if (!opt) return 0;
  return opt.price_usd + (form.extended_stay && offering.extended_stay ? offering.extended_stay.price_usd : 0);
};

export function RoomSelection({ offering, form, update, availability, invite }) {
  const ext = offering.extended_stay;
  const selected = (offering.room_options || []).find((o) => o.key === form.room_key);
  return (
    <div className="bg-white border border-aviana-border rounded-[1.75rem] p-7 md:p-9 space-y-6" data-field="room_key" data-testid="room-selection">
      <header className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0"><BedDouble size={18} /></div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl text-aviana-ink leading-tight">Choose where you'll rest</h2>
          <p className="mt-1 text-aviana-muted text-[14px]">Four cozy suites, each with its own private bathroom.</p>
        </div>
      </header>

      {invite ? (
        <div className="rounded-2xl bg-aviana-cream border border-aviana-gold/40 p-5" data-testid="roommate-invite-banner">
          <p className="overline-plum mb-1 flex items-center gap-2"><Sparkles size={13} /> Your spot is being held</p>
          <p className="text-aviana-inkSoft text-[14px] leading-[1.7]">
            {invite.available
              ? <>You're joining <strong>{invite.host_name}</strong> in the <strong>{invite.room_label}</strong>. Complete the form below to claim the second spot.</>
              : <>This shared-suite spot has already been claimed. Please reach out to Holly or choose a room on the retreat page.</>}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {offering.room_options.map((o) => {
            const open = availability?.[o.key] !== false;
            const on = form.room_key === o.key;
            return (
              <button type="button" key={o.key} disabled={!open} onClick={() => update({ room_key: o.key })}
                data-testid={`room-option-${o.key}`}
                className={`text-left p-5 rounded-2xl border transition-colors ${on ? "border-aviana-ink bg-aviana-cream" : "border-aviana-border hover:border-aviana-plum/40"} ${open ? "" : "opacity-50 cursor-not-allowed"}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-display text-2xl text-aviana-ink">{o.label}</p>
                  <p className="font-display text-2xl text-aviana-plum whitespace-nowrap">${o.price_usd.toLocaleString()}<span className="text-[11px] tracking-[0.15em] uppercase text-aviana-muted ml-1">{o.per_person ? "/ person" : ""}</span></p>
                </div>
                <p className="mt-2 text-[13px] text-aviana-muted leading-[1.6]">{o.blurb}</p>
                {!open && <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-aviana-plum" data-testid={`room-full-${o.key}`}>This suite is full</p>}
              </button>
            );
          })}
        </div>
      )}

      {!invite && selected?.shared_pair && (
        <div className="rounded-2xl border border-aviana-border p-5 space-y-4" data-testid="roommate-fields">
          <label className="block" data-field="roommate_name">
            <span className="overline-plum mb-2 block">Who is sharing your suite? *</span>
            <input className={inputCls} value={form.roommate_name} onChange={(e) => update({ roommate_name: e.target.value })} placeholder="Her full name" data-testid="roommate-name-input" />
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="mt-1 accent-aviana-plum" checked={form.invite_roommate} onChange={(e) => update({ invite_roommate: e.target.checked })} data-testid="invite-roommate-checkbox" />
            <span className="text-[14px] text-aviana-inkSoft leading-[1.6] flex items-center gap-2"><Mail size={14} className="text-aviana-plum" /> Email her a link to register for the second spot — we'll hold it for her.</span>
          </label>
          {form.invite_roommate && (
            <label className="block" data-field="roommate_email">
              <span className="overline-plum mb-2 block">Her email *</span>
              <input type="email" className={inputCls} value={form.roommate_email} onChange={(e) => update({ roommate_email: e.target.value })} data-testid="roommate-email-input" />
            </label>
          )}
        </div>
      )}

      {ext && (
        <label className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer transition-colors ${form.extended_stay ? "border-aviana-ink bg-aviana-cream" : "border-aviana-border"}`} data-testid="extended-stay-option">
          <input type="checkbox" className="mt-1.5 accent-aviana-plum" checked={form.extended_stay} onChange={(e) => update({ extended_stay: e.target.checked })} data-testid="extended-stay-checkbox" />
          <span>
            <span className="font-display text-xl text-aviana-ink flex items-center gap-2"><Moon size={16} className="text-aviana-gold" /> {ext.label} · +${ext.price_usd} per person</span>
            <span className="block mt-1.5 text-[13px] text-aviana-muted leading-[1.6]">{ext.blurb}</span>
          </span>
        </label>
      )}
    </div>
  );
}

export function PaymentPlanPicker({ offering, form, update, balance }) {
  const max = offering.payment_plan_max || 0;
  if (!max || form.payment_type !== "deposit" || balance <= 0) return null;
  return (
    <div className="mt-5" data-testid="payment-plan-picker">
      <p className="overline-plum mb-3">Pay the remaining ${balance.toLocaleString()} in</p>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button type="button" key={n} onClick={() => update({ payment_plan_installments: n })} data-testid={`plan-${n}`}
            className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.15em] uppercase border transition-colors ${(form.payment_plan_installments || 1) === n ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"}`}>
            {n === 1 ? "1 payment" : `${n} installments · $${(balance / n).toFixed(2)}`}
          </button>
        ))}
      </div>
      <p className="mt-3 text-[12px] text-aviana-muted">Full balance due by {offering.balance_due_label}. Holly will send each installment by email — pay by card, Venmo or Zelle.</p>
    </div>
  );
}
